import type { QueryClient } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import type { MutableRefObject } from 'react'
import { useCallback } from 'react'
import type { ReportingChartQuestionContext } from 'src/agent/reporting/chartQuestion.types'
import { toReportingChartContextRef } from 'src/agent/reporting/chartQuestion.types'
import { reportingAgentQueryKeys } from 'src/queries/reportingAgent/reportingAgent.keys'
import { reportingAgentMutations } from 'src/queries/reportingAgent/reportingAgent.mutations'

import {
  isReportingAccessDenied,
  isReportingIdempotencyConflict,
  isReportingTransientError,
  newReportingIdempotencyKey,
  reportingErrorCode,
  reportingErrorStatus,
} from './reportingConversation.errors'
import type {
  PendingOperation,
  ReportingSubmission,
} from './reportingConversation.types'
import type {
  ReportingDraftAction,
  ReportingDraftState,
} from './reportingDraft.reducer'

interface UseReportingConversationSubmissionOptions {
  actorId?: string
  organizationId?: string
  conversationId?: string
  isAuthenticated: boolean
  draftState: ReportingDraftState
  draftStateRef: MutableRefObject<ReportingDraftState>
  foregroundRunIdRef: MutableRefObject<string | null>
  pendingOperationRef: MutableRefObject<PendingOperation | null>
  acceptedConversationRef: MutableRefObject<string | null>
  onConversationAcceptedRef: MutableRefObject<(conversationId: string) => void>
  accessDeniedRef: MutableRefObject<boolean>
  denyAccess: (status: number) => void
  dispatchDraft: (action: ReportingDraftAction) => void
  queryClient: QueryClient
  refetchMessages: () => Promise<unknown>
}

export const useReportingConversationSubmission = ({
  actorId,
  organizationId,
  conversationId,
  isAuthenticated,
  draftState,
  draftStateRef,
  foregroundRunIdRef,
  pendingOperationRef,
  acceptedConversationRef,
  onConversationAcceptedRef,
  accessDeniedRef,
  denyAccess,
  dispatchDraft,
  queryClient,
  refetchMessages,
}: UseReportingConversationSubmissionOptions) => {
  const createDraft = useMutation(reportingAgentMutations.createDraft())
  const sendMessage = useMutation(reportingAgentMutations.sendMessage())

  const acceptConversation = useCallback(
    (submission: ReportingSubmission, shouldNotify: boolean) => {
      pendingOperationRef.current = null
      const currentDraftState = draftStateRef.current
      dispatchDraft({
        type: 'submissionAccepted',
        conversationId:
          'conversation_id' in submission
            ? submission.conversation_id
            : (currentDraftState.conversationId ?? conversationId ?? ''),
        messageId: submission.message_id,
        runId: submission.run_id,
        status: submission.status,
      })
      if (
        shouldNotify &&
        'conversation_id' in submission &&
        acceptedConversationRef.current !== submission.conversation_id
      ) {
        acceptedConversationRef.current = submission.conversation_id
        onConversationAcceptedRef.current(submission.conversation_id)
      }
    },
    [
      acceptedConversationRef,
      conversationId,
      dispatchDraft,
      draftStateRef,
      onConversationAcceptedRef,
      pendingOperationRef,
    ],
  )

  return useCallback(
    (prompt: string, chartContext?: ReportingChartQuestionContext) => {
      if (
        !actorId ||
        !organizationId ||
        !isAuthenticated ||
        accessDeniedRef.current ||
        pendingOperationRef.current
      ) {
        return
      }
      const currentConversationId = draftState.conversationId ?? conversationId
      if (!currentConversationId) {
        const idempotencyKey = draftState.draftRequestId
        if (!idempotencyKey) return
        const operation: PendingOperation = {
          kind: 'create',
          prompt,
          chartContext,
        }
        pendingOperationRef.current = operation
        dispatchDraft({ type: 'submitStarted', prompt, idempotencyKey })
        createDraft.mutate(
          {
            organizationId,
            question: prompt,
            idempotencyKey,
            chartContext: chartContext
              ? toReportingChartContextRef(chartContext)
              : undefined,
          },
          {
            onSuccess: (submission) => acceptConversation(submission, true),
            onError: (error) => {
              pendingOperationRef.current = null
              if (isReportingAccessDenied(error)) {
                denyAccess(reportingErrorStatus(error) ?? 403)
                return
              }
              dispatchDraft({
                type: 'runFailed',
                code: isReportingIdempotencyConflict(error)
                  ? 'idempotency_conflict'
                  : 'submission_failed',
                message: 'The report could not be completed.',
                retryable: isReportingTransientError(error),
              })
            },
          },
        )
        return
      }

      const idempotencyKey =
        draftState.messageIdempotencyKey ?? newReportingIdempotencyKey()
      const operation: PendingOperation = {
        kind: 'refine',
        prompt,
        chartContext,
      }
      pendingOperationRef.current = operation
      dispatchDraft({
        type: 'refinementStarted',
        prompt,
        idempotencyKey,
        runId: `pending-${idempotencyKey}`,
      })
      sendMessage.mutate(
        {
          organizationId,
          conversationId: currentConversationId,
          content: prompt,
          idempotencyKey,
          referencedArtifactId: draftState.artifact?.artifact_id,
          chartContext: chartContext
            ? toReportingChartContextRef(chartContext)
            : undefined,
        },
        {
          onSuccess: (submission) => {
            foregroundRunIdRef.current = submission.run_id
            acceptConversation(submission, false)
            void refetchMessages()
          },
          onError: (error) => {
            pendingOperationRef.current = null
            if (reportingErrorCode(error) === 'conversation_run_active') {
              dispatchDraft({
                type: 'activeRunConflict',
                runId: draftState.runId ?? 'active',
              })
              void queryClient.invalidateQueries({
                queryKey: reportingAgentQueryKeys.messages(
                  actorId,
                  organizationId,
                  currentConversationId,
                ),
              })
              void queryClient.invalidateQueries({
                queryKey: reportingAgentQueryKeys.run(
                  actorId,
                  organizationId,
                  draftState.runId ?? undefined,
                ),
              })
              return
            }
            if (isReportingAccessDenied(error)) {
              denyAccess(reportingErrorStatus(error) ?? 403)
              return
            }
            dispatchDraft({
              type: 'runFailed',
              code: isReportingIdempotencyConflict(error)
                ? 'idempotency_conflict'
                : 'refinement_failed',
              message: 'The report could not be completed.',
              retryable: isReportingTransientError(error),
            })
          },
        },
      )
    },
    [
      acceptConversation,
      accessDeniedRef,
      actorId,
      conversationId,
      createDraft,
      denyAccess,
      dispatchDraft,
      draftState,
      foregroundRunIdRef,
      isAuthenticated,
      organizationId,
      pendingOperationRef,
      queryClient,
      refetchMessages,
      sendMessage,
    ],
  )
}
